'use client';
import { useDebounceFn } from 'ahooks';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { useGlobalModal } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import { cn, reportEvent } from '@/app/utils';

export default function Newsletter() {
  const t = useTranslations('home3.newsletter');

  const userInfo = useStore((state) => state.userInfo);
  const { open: openNewsletterSubscriptionDialog } = useGlobalModal('newsletterSubscriptionDialog');
  const [email, setEmail] = useState('');
  const [isEmailValid, setIsEmailValid] = useState(false);

  // 邮箱验证
  const { run: checkEmail } = useDebounceFn(
    (email: string) => {
      if (!email) {
        setIsEmailValid(false);
        return;
      }
      if (/^[\w.-]+@[\w-]+(\.[\w-]+)+$/.test(email)) {
        setIsEmailValid(true);
      } else {
        setIsEmailValid(false);
      }
    },
    { wait: 500 },
  );

  // 处理订阅点击
  const handleSubscribe = () => {
    const initialEmail = userInfo?.email || email;

    // 登录 or 邮箱有效，才打开弹窗
    if (userInfo || isEmailValid) {
      openNewsletterSubscriptionDialog({
        initialEmail,
        from_page: 'landing_page',
      });

      reportEvent('element_click', {
        element_name: 'email_subscribe_button_click',
        from_page: 'landing_page',
      });
    }
  };

  return (
    <section className="relative bg-black pb-20 md:py-16">
      <div className="mx-auto max-w-6xl px-6 md:px-4">
        <div className="text-center">
          {/* 标题 */}
          <h2 className="mb-2 font-medium text-[42px] text-white md:text-3xl">{t('title')}</h2>

          {/* 副标题 */}
          <h3 className="mb-8 font-normal text-lg text-system-text03 md:text-lg">{t('subTitle')}</h3>

          {/* 邮箱输入和订阅按钮 */}
          <div className="relative mx-auto mb-4 max-w-lg">
            <div
              className="relative rounded-[50px] border border-white/40 backdrop-blur-[10px]"
              style={{
                background: 'rgba(255, 255, 255, 0.24)',
              }}
            >
              <Input
                type="email"
                placeholder={t('placeholder')}
                value={userInfo?.email || email}
                disabled={!!userInfo}
                onChange={(e) => {
                  if (!userInfo) {
                    setEmail(e.target.value);
                    checkEmail(e.target.value);
                  }
                }}
                className={cn(
                  'h-14 rounded-[50px] border-0 bg-transparent px-6 pr-32 text-white placeholder:text-system-white40 focus-visible:ring-0 focus-visible:ring-offset-0',
                  userInfo && 'cursor-not-allowed opacity-80',
                )}
              />

              <Button
                onClick={handleSubscribe}
                disabled={!userInfo && !isEmailValid}
                className="absolute top-2 right-2 h-10 rounded-full bg-blue-600 px-6 font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {t('button')}
              </Button>
            </div>
          </div>

          {/* 描述文字 */}
          <p className="mx-auto font-normal text-base text-system-text03/60">{t('description')}</p>
        </div>
      </div>
    </section>
  );
}
